import { type Include, type Surgery, type SurgeryTypeGroup as StormSurgeryTypeGroup } from '~/storm'

/**
 * Storm-typed surgery type group with the relations `getSurgeryCategoryName`
 * reads: `surgeryTypes` (for `external_surgery_type_id`), `subgroups`
 * (for nested-category resolution), and `specialities` (for narrowing when
 * multiple categories match). Denormalizer `SurgeryTypeGroup[]` and the
 * `getSurgeryTypeGroup.all` getter from `~/store/selectors` satisfy it
 * structurally (§10 of the shallow-selectors documentation).
 */
export type SurgeryTypeGroupForCategoryName = Include<StormSurgeryTypeGroup, { surgeryTypes: true; specialities: true; subgroups: true }>

function categoryResolvesSurgeryType(
    category: SurgeryTypeGroupForCategoryName,
    surgeryTypeId: number,
    groupsById: Map<number, SurgeryTypeGroupForCategoryName>
): boolean {
    const visited = new Set<number>()
    const stack: SurgeryTypeGroupForCategoryName[] = [category]
    while (stack.length > 0) {
        const current = stack.pop()
        if (!current || visited.has(current.id)) continue
        visited.add(current.id)
        for (const association of current.surgeryTypes) {
            if (association.external_surgery_type_id === surgeryTypeId) return true
        }
        for (const subgroupRef of current.subgroups) {
            const subgroup = groupsById.get(subgroupRef.id)
            if (subgroup) stack.push(subgroup)
        }
    }
    return false
}

/**
 * Returns the display name of the (top-level) surgery category that contains
 * `surgery.surgeryType`. Walks each category's nested subgroups by id through
 * `surgeryTypeGroups` so a surgery whose type is attached to a deeply nested
 * child still resolves to the top-level category — mirroring
 * `resolveSurgeryTypeGroups` in
 * `storm/common/resolveSurgeryTypeGroups.ts`.
 *
 * When multiple categories match the same `external_surgery_type_id` (e.g.
 * generic types like "Andre operasjoner/inngrep" attached to several
 * categories), the surgery's `section.speciality_id` is used to pick the
 * category whose `specialities` covers that section.
 */
type SurgeryWithSection = Include<Surgery, { section: true }>

function hasSection(surgery: Surgery): surgery is SurgeryWithSection {
    return 'section' in surgery
}

function getSectionSpecialityId(surgery: Surgery): number | undefined {
    return hasSection(surgery) ? surgery.section?.speciality_id : undefined
}

export function getSurgeryCategoryName(surgery: Surgery, surgeryTypeGroups: SurgeryTypeGroupForCategoryName[]): string | undefined {
    const surgeryTypeId = surgery.surgeryType?.id
    if (!surgeryTypeId) return undefined

    const groupsById = new Map(surgeryTypeGroups.map(group => [group.id, group]))

    const matchingCategories: SurgeryTypeGroupForCategoryName[] = []
    for (const group of surgeryTypeGroups) {
        if (group.group_type !== 'category') continue
        if (categoryResolvesSurgeryType(group, surgeryTypeId, groupsById)) {
            matchingCategories.push(group)
        }
    }

    if (matchingCategories.length > 1) {
        const specialityId = getSectionSpecialityId(surgery)
        const bySpeciality = matchingCategories.filter(category => category.specialities.some(speciality => speciality.id === specialityId))
        if (bySpeciality.length > 1) {
            console.warn('Surgery type has multiple surgery categories', {
                surgeryTypeId,
                specialityId,
                categoryIds: bySpeciality.map(c => c.id),
            })
        }
        return bySpeciality.at(0)?.display_name
    }

    return matchingCategories.at(0)?.display_name
}
